import { useNavigate } from '@tanstack/react-router';
import { MoreHorizontal } from 'lucide-react';
import { propertyStatusOptions, type ChannexUnitPublicationRow } from '@/shared/api';
import { ActionMenu, Button, type ActionMenuItem } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { lifecycleAction, presentUnit, type LifecycleAction } from './publicationModel';
import { PublicationStateChip } from './PublicationStateChip';

/** Spalten mit und ohne Auswahl — schmal zweizeilig, ab 36 rem eine Zeile je Apartment. */
export const UNIT_COLUMNS = {
  select:
    'grid-cols-[1rem_minmax(0,1fr)_auto] @xl:grid-cols-[1rem_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,0.7fr)_2.75rem]',
  plain:
    'grid-cols-[minmax(0,1fr)_auto] @xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,0.7fr)_2.75rem]',
};

interface Props {
  unit: ChannexUnitPublicationRow;
  /** Nur wer veröffentlichen darf und etwas zur Auswahl hat, sieht die Auswahlspalte. */
  withSelect: boolean;
  checked: boolean;
  selectable: boolean;
  canEdit: boolean;
  /** Nur der Plattform-Admin gleicht eine mehrdeutige Bindung ab — so sagt es der Endpunkt. */
  canReconcile: boolean;
  busy: boolean;
  onToggle: () => void;
  onLifecycle: (action: NonNullable<LifecycleAction>) => void;
  onReconcile: () => void;
}

export function PublicationUnitRow(props: Props) {
  const { unit, withSelect, canEdit, busy } = props;
  const navigate = useNavigate();
  const presentation = presentUnit(unit.state);
  const lifecycle = canEdit ? lifecycleAction(unit.lifecycleStatus) : null;
  const items: ActionMenuItem[] = [
    ...(lifecycle
      ? [
          {
            label: lifecycle === 'pause' ? 'Verkauf pausieren …' : 'Verkauf fortsetzen …',
            icon: lifecycle === 'pause' ? ('Pause' as const) : ('Play' as const),
            disabled: busy,
            onClick: () => props.onLifecycle(lifecycle),
          },
        ]
      : []),
    {
      label: 'Apartment öffnen',
      icon: 'ExternalLink',
      onClick: () => void navigate({ to: '/properties/$id', params: { id: unit.propertyId } }),
    },
  ];

  return (
    <li
      className={cn(
        '@xl:py-1.5 grid items-center gap-x-4 gap-y-1.5 px-5 py-3',
        withSelect ? UNIT_COLUMNS.select : UNIT_COLUMNS.plain,
      )}
    >
      {withSelect && (
        // 44 px Trefferfläche um das 16-px-Kästchen, ohne die Spalte zu verbreitern (§9).
        <label className="-m-3.5 grid size-11 cursor-pointer place-items-center has-[:disabled]:cursor-default">
          <input
            type="checkbox"
            className="accent-sage size-4"
            checked={props.checked}
            disabled={!props.selectable}
            onChange={props.onToggle}
            aria-label={`${unit.name} auswählen`}
          />
        </label>
      )}
      <div className="min-w-0">
        <p className="text-ink truncate text-sm font-medium">{unit.name}</p>
        {presentation.hint && <p className="text-ink-soft mt-0.5 text-xs">{presentation.hint}</p>}
        {unit.lastError && <p className="text-sealwax mt-0.5 text-xs">{unit.lastError}</p>}
        {/*
          Eine gesperrte Bindung blockiert jeden weiteren Push des Objekts. Der Weg heraus ist kein
          Wiederholen (§6.102), sondern eine Entscheidung — deshalb ein eigener Knopf, der einen
          Dialog öffnet, und nur für den Plattform-Admin.
        */}
        {props.canReconcile && unit.state === 'NeedsReconcile' && unit.bindingId && (
          <Button
            variant="brand"
            size="sm"
            className="mt-2 min-h-11"
            disabled={busy}
            onClick={props.onReconcile}
          >
            Abgleichen
          </Button>
        )}
      </div>
      {/* Schmal steht der Stand in einer zweiten Zeile unter dem Namen; die Reihenfolge im
          Dokument bleibt die sichtbare der breiten Zeile — Name, Stand, Menü. */}
      <div
        className={cn(
          '@xl:contents row-start-2 flex flex-wrap items-center gap-x-3 gap-y-1',
          withSelect ? 'col-start-2' : 'col-start-1',
        )}
      >
        <PublicationStateChip presentation={presentation} />
        <p className="text-ink text-sm">
          <span className="text-ink-soft @xl:sr-only">Verkauf: </span>
          {propertyStatusOptions.find((option) => option.value === unit.lifecycleStatus)?.label ??
            unit.lifecycleStatus}
        </p>
      </div>
      <div
        className={cn(
          '@xl:col-start-auto @xl:row-start-auto row-start-1 flex justify-end',
          withSelect ? 'col-start-3' : 'col-start-2',
        )}
      >
        <ActionMenu
          items={items}
          trigger={
            <Button
              variant="ghost"
              size="icon"
              className="size-11"
              aria-label={`Aktionen für ${unit.name}`}
            >
              <MoreHorizontal size={18} />
            </Button>
          }
        />
      </div>
    </li>
  );
}
